import { useEffect, useState } from 'react';
import { HandThumbUpIcon, HandThumbDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (rating: 'up' | 'down', comment?: string) => Promise<void>;
  initialRating?: 'up' | 'down';
}

const FeedbackModal = ({ open, onClose, onSubmit, initialRating }: Props) => {
  const [rating, setRating] = useState<'up' | 'down' | null>(initialRating ?? null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // When opening, seed rating from initialRating if provided
    if (open) {
      setRating(initialRating ?? null);
    }
  }, [open, initialRating]);

  if (!open) return null;

  const submit = async () => {
    if (!rating) return;
    setIsSubmitting(true);
    try {
      await onSubmit(rating, comment.trim().length > 0 ? comment : undefined);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Give Feedback</h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-500 hover:bg-slate-100">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <p className="mt-2 text-sm text-slate-600">Rate your experience and leave an optional comment.</p>

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setRating('up')}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold shadow-sm transition ${
              rating === 'up' ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-300 bg-white text-slate-900'
            }`}
          >
            <HandThumbUpIcon className="h-5 w-5" />
            Helpful
          </button>
          <button
            type="button"
            onClick={() => setRating('down')}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold shadow-sm transition ${
              rating === 'down' ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-300 bg-white text-slate-900'
            }`}
          >
            <HandThumbDownIcon className="h-5 w-5" />
            Not Helpful
          </button>
        </div>

        {rating && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700">Comments (optional)</label>
            <textarea
              className="mt-1 w-full rounded-xl border border-slate-300 p-3 text-sm shadow-sm focus:border-slate-400 focus:outline-none"
              rows={4}
              placeholder="What worked well? What can we improve?"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-900 shadow-sm">
            Cancel
          </button>
          <button
            type="button"
            disabled={!rating || isSubmitting}
            onClick={submit}
            className="btn-primary disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
